// frontend/src/pages/TasksPage.tsx

import { useState, useCallback, useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Button,
  Stack,
  Paper,
  Modal,
  Loader,
  Alert,
  Tabs,
  Badge,
  Card,
  ActionIcon,
  Menu,
  Anchor,
  Switch,
  SegmentedControl,
  Tooltip,
  Collapse,
  ScrollArea,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconPlus,
  IconAlertCircle,
  IconCheck,
  IconCalendarEvent,
  IconTemplate,
  IconHistory,
  IconDotsVertical,
  IconPencil,
  IconTrash,
  IconBell,
  IconArchive,
  IconArchiveOff,
  IconCircleCheck,
  IconChevronUp,
  IconChevronDown,
} from '@tabler/icons-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTank } from '@/contexts/TankContext';
import { NoTankSelected } from '@/components/common/NoTankSelected';
import {
  useTaskInstances,
  useTaskDefinitions,
  useCreateTaskDefinition,
  useUpdateTaskDefinition,
  usePauseTaskDefinition,
  useResumeTaskDefinition,
  useArchiveTaskDefinition,
  useRestoreTaskDefinition,
  useDeleteTaskDefinition,
  useCompleteTask,
  useSnoozeTask,
  useSkipTask,
  useLogCompletion,
  useTaskCompletions,
} from '@/hooks/useTasks';
import { TaskDefinitionForm } from '@/features/tasks/TaskDefinitionForm';
import { TaskInstanceCard } from '@/features/tasks/TaskInstanceCard';
import { CompleteTaskModal } from '@/features/tasks/CompleteTaskModal';
import { SnoozeTaskModal } from '@/features/tasks/SnoozeTaskModal';
import { LogCompletionModal } from '@/features/tasks/LogCompletionModal';
import type { LogCompletionData } from '@/features/tasks/LogCompletionModal';
import { TaskCompletionHistory } from '@/features/tasks/TaskCompletionHistory';
import { TaskActivityTimeline } from '@/features/tasks/TaskActivityTimeline';
import type { DatesRangeValue } from '@mantine/dates';
import { NotificationOnboardingModal } from '@/components/NotificationOnboardingModal';
import { useNotificationOnboarding } from '@/hooks/useNotificationOnboarding';
import type {
  TaskInstance,
  TaskDefinition,
  TaskDefinitionCreate,
  CompleteTaskData,
} from '@/types/task';
import {
  TASK_TYPE_LABELS,
  TASK_TYPE_COLORS,
  TASK_CATEGORY_MAP,
  isRecurring,
  isRollingRecurrence,
  getScheduleLabel,
  getCompactScheduleLabel,
  type TaskCategory,
} from '@/types/task';

type TabValue = 'dashboard' | 'templates' | 'history';

const VALID_TABS: readonly string[] = ['dashboard', 'templates', 'history'];

function isValidTab(value: string | null): value is TabValue {
  return value !== null && VALID_TABS.includes(value);
}

interface TaskGroup {
  key: string;
  label: string;
  color: string;
  instances: TaskInstance[];
}

function groupInstancesByUrgency(instances: TaskInstance[]): TaskGroup[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const overdue: TaskInstance[] = [];
  const dueToday: TaskInstance[] = [];
  const upcoming: TaskInstance[] = [];

  for (const inst of instances) {
    const due = new Date(inst.due_at);
    if (due < today) {
      overdue.push(inst);
    } else if (due < tomorrow) {
      dueToday.push(inst);
    } else {
      upcoming.push(inst);
    }
  }

  const groups: TaskGroup[] = [];
  if (overdue.length > 0) {
    groups.push({ key: 'overdue', label: 'Overdue', color: 'red', instances: overdue });
  }
  if (dueToday.length > 0) {
    groups.push({ key: 'due-today', label: 'Due Today', color: 'orange', instances: dueToday });
  }
  if (upcoming.length > 0) {
    groups.push({ key: 'upcoming', label: 'Upcoming', color: 'blue', instances: upcoming });
  }
  return groups;
}

function SectionGroup({
  group,
  isFixedSchedule,
  getInstanceScheduleLabel,
  getInstanceCompactScheduleLabel,
  onComplete,
  onSnooze,
  onSkip,
  onEditTask,
  initialLimit,
  expanded,
  onExpand,
  pageSize,
  currentPage,
  onPageChange,
}: {
  group: TaskGroup;
  isFixedSchedule: (instance: TaskInstance) => boolean;
  getInstanceScheduleLabel: (instance: TaskInstance) => string | null;
  getInstanceCompactScheduleLabel: (instance: TaskInstance) => string | null;
  onComplete: (instance: TaskInstance) => void;
  onSnooze: (instance: TaskInstance) => void;
  onSkip: (instance: TaskInstance) => void;
  onEditTask: (instance: TaskInstance) => void;
  initialLimit?: number;
  expanded?: boolean;
  onExpand?: () => void;
  pageSize?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
}) {
  const [opened, { toggle }] = useDisclosure(true);

  const needsPagination = expanded && pageSize && group.instances.length > pageSize;
  const totalPages = needsPagination ? Math.ceil(group.instances.length / pageSize) : 1;
  const page = currentPage ?? 1;

  let visibleInstances: TaskInstance[];
  if (initialLimit && !expanded) {
    // Collapsed: show first N
    visibleInstances = group.instances.slice(0, initialLimit);
  } else if (needsPagination && pageSize) {
    // Expanded + paginated
    const start = (page - 1) * pageSize;
    visibleInstances = group.instances.slice(start, start + pageSize);
  } else {
    // Expanded, no pagination needed
    visibleInstances = group.instances;
  }

  const hasMore = initialLimit && !expanded && group.instances.length > initialLimit;

  return (
    <Paper withBorder style={{ overflow: 'hidden' }}>
      <Group
        justify="space-between"
        p="xs"
        px="sm"
        style={{
          cursor: 'pointer',
          backgroundColor: 'var(--mantine-color-gray-0)',
          borderBottom: opened ? '1px solid var(--mantine-color-default-border)' : 'none',
        }}
        onClick={toggle}
      >
        <Group gap="xs">
          <Text size="sm" fw={600}>{group.label}</Text>
          <Badge size="xs" color={group.color} variant="filled">
            {group.instances.length}
          </Badge>
        </Group>
        <ActionIcon variant="subtle" size="sm">
          {opened ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
        </ActionIcon>
      </Group>
      <Collapse in={opened}>
        {visibleInstances.map((instance) => (
          <TaskInstanceCard
            key={instance.id}
            instance={instance}
            isFixed={isFixedSchedule(instance)}
            scheduleLabel={getInstanceScheduleLabel(instance)}
            compactScheduleLabel={getInstanceCompactScheduleLabel(instance)}
            onComplete={onComplete}
            onSnooze={onSnooze}
            onSkip={onSkip}
            onEditTask={onEditTask}
          />
        ))}
        {hasMore && (
          <Group justify="center" p="sm">
            <Button
              variant="subtle"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onExpand?.();
              }}
            >
              View all {group.instances.length} upcoming tasks
            </Button>
          </Group>
        )}
        {needsPagination && (
          <Group justify="center" p="sm" gap="md">
            <Button
              variant="subtle"
              size="xs"
              disabled={page <= 1}
              onClick={() => onPageChange?.(page - 1)}
            >
              Previous
            </Button>
            <Text size="sm" c="dimmed">
              Page {page} of {totalPages}
            </Text>
            <Button
              variant="subtle"
              size="xs"
              disabled={page >= totalPages}
              onClick={() => onPageChange?.(page + 1)}
            >
              Next
            </Button>
          </Group>
        )}
      </Collapse>
    </Paper>
  );
}

export default function TasksPage() {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const { selectedTank } = useTank();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabValue = isValidTab(tabParam) ? tabParam : 'dashboard';

  const [upcomingExpanded, setUpcomingExpanded] = useState(false);
  const [upcomingPage, setUpcomingPage] = useState(1);

  // Activity timeline state
  const [timelineDateRange, setTimelineDateRange] = useState<DatesRangeValue>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 90);
    return [start, end];
  });

  const timelineCompletionFilters = useMemo(() => {
    if (!timelineDateRange[0] || !timelineDateRange[1]) return {};
    const start = timelineDateRange[0] instanceof Date ? timelineDateRange[0].toISOString() : timelineDateRange[0];
    const end = timelineDateRange[1] instanceof Date ? timelineDateRange[1].toISOString() : timelineDateRange[1];
    return { start_date: start, end_date: end };
  }, [timelineDateRange]);

  const { data: timelineCompletionsData } = useTaskCompletions(timelineCompletionFilters);

  const handleTabChange = useCallback((value: string | null) => {
    if (value && value !== 'dashboard') {
      setSearchParams({ tab: value }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [setSearchParams]);
  const [formOpened, { open: openForm, close: closeForm }] = useDisclosure(false);
  const [editingDefinition, setEditingDefinition] = useState<TaskDefinition | null>(null);
  const [formDirty, setFormDirty] = useState(false);
  const [unsavedWarningOpen, { open: openUnsavedWarning, close: closeUnsavedWarning }] = useDisclosure(false);
  const [archiveConfirmId, setArchiveConfirmId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Category sub-tab state
  const [categoryFilter, setCategoryFilter] = useState<'all' | TaskCategory>('all');
  const [showArchived, setShowArchived] = useState(false);

  // Modal state for task actions
  const [completeModalInstance, setCompleteModalInstance] = useState<TaskInstance | null>(null);
  const [snoozeModalInstance, setSnoozeModalInstance] = useState<TaskInstance | null>(null);
  const [logCompletionDefinition, setLogCompletionDefinition] = useState<TaskDefinition | null>(null);

  // Fetch data
  const {
    data: instancesData,
    isLoading: instancesLoading,
    error: instancesError,
  } = useTaskInstances();

  const {
    data: definitionsData,
    isLoading: definitionsLoading,
    error: definitionsError,
  } = useTaskDefinitions();

  // Mutations
  const createMutation = useCreateTaskDefinition();
  const updateMutation = useUpdateTaskDefinition();
  const pauseMutation = usePauseTaskDefinition();
  const resumeMutation = useResumeTaskDefinition();
  const archiveMutation = useArchiveTaskDefinition();
  const restoreMutation = useRestoreTaskDefinition();
  const deleteMutation = useDeleteTaskDefinition();
  const completeMutation = useCompleteTask();
  const snoozeMutation = useSnoozeTask();
  const skipMutation = useSkipTask();
  const logCompletionMutation = useLogCompletion();

  // Notification onboarding - uses task definition count to trigger modal
  const taskCount = definitionsData?.definitions.length ?? 0;
  const {
    modalOpen: onboardingModalOpen,
    closeModal: closeOnboardingModal,
    shouldShowReminder,
  } = useNotificationOnboarding({ taskCount });

  // Handlers
  const handleOpenCreate = () => {
    setEditingDefinition(null);
    setFormDirty(false);
    openForm();
  };

  const handleEditDefinition = (definition: TaskDefinition) => {
    setEditingDefinition(definition);
    openForm();
  };

  const handleFormSubmit = (formData: TaskDefinitionCreate, desiredStatus?: 'active' | 'paused') => {
    if (editingDefinition) {
      updateMutation.mutate(
        { id: editingDefinition.id, data: formData },
        {
          onSuccess: () => {
            // Handle status change (pause/resume) after successful update
            if (desiredStatus && desiredStatus !== editingDefinition.status) {
              if (desiredStatus === 'paused') {
                pauseMutation.mutate(editingDefinition.id);
              } else {
                resumeMutation.mutate(editingDefinition.id);
              }
            }
            setFormDirty(false);
            closeForm();
            setEditingDefinition(null);
          },
        }
      );
    } else {
      createMutation.mutate(formData, {
        onSuccess: () => {
          setFormDirty(false);
          closeForm();
          setEditingDefinition(null);
        },
      });
    }
  };

  const handleFormCancel = () => {
    if (formDirty) {
      openUnsavedWarning();
    } else {
      setFormDirty(false);
      closeForm();
      setEditingDefinition(null);
    }
  };

  const handleDiscardChanges = () => {
    closeUnsavedWarning();
    setFormDirty(false);
    closeForm();
    setEditingDefinition(null);
  };

  // Task instance actions
  const handleComplete = (instance: TaskInstance) => {
    setCompleteModalInstance(instance);
  };

  const handleSnooze = (instance: TaskInstance) => {
    setSnoozeModalInstance(instance);
  };

  const handleSkip = (instance: TaskInstance) => {
    skipMutation.mutate(instance.id);
  };

  // Look up definition for an instance and open edit modal
  const handleEditTaskFromInstance = (instance: TaskInstance) => {
    const definition = definitionsData?.definitions.find(
      (d) => d.id === instance.definition_id
    );
    if (definition) {
      setCompleteModalInstance(null); // Close complete modal if open
      handleEditDefinition(definition);
    }
  };

  const handleCompleteSubmit = (data: CompleteTaskData) => {
    if (completeModalInstance) {
      completeMutation.mutate(
        { instanceId: completeModalInstance.id, data },
        { onSuccess: () => setCompleteModalInstance(null) }
      );
    }
  };

  const handleSnoozeSubmit = (snoozeUntil: string) => {
    if (snoozeModalInstance) {
      snoozeMutation.mutate(
        { instanceId: snoozeModalInstance.id, data: { snooze_until: snoozeUntil } },
        { onSuccess: () => setSnoozeModalInstance(null) }
      );
    }
  };

  const handleLogCompletion = (definition: TaskDefinition) => {
    setLogCompletionDefinition(definition);
  };

  const handleLogCompletionSubmit = (data: LogCompletionData) => {
    if (logCompletionDefinition) {
      logCompletionMutation.mutate(
        { definitionId: logCompletionDefinition.id, data },
        { onSuccess: () => setLogCompletionDefinition(null) }
      );
    }
  };

  const isFixedSchedule = (instance: TaskInstance): boolean => {
    const definition = definitionsData?.definitions.find(
      (d) => d.id === instance.definition_id
    );
    if (!definition?.recurrence) return false;
    return !isRollingRecurrence(definition.recurrence);
  };

  const getInstanceScheduleLabel = (instance: TaskInstance): string | null => {
    const definition = definitionsData?.definitions.find(
      (d) => d.id === instance.definition_id
    );
    return getScheduleLabel(definition?.recurrence);
  };

  const getInstanceCompactScheduleLabel = (instance: TaskInstance): string | null => {
    const definition = definitionsData?.definitions.find(
      (d) => d.id === instance.definition_id
    );
    return getCompactScheduleLabel(definition?.recurrence);
  };

  // Loading state
  const isLoading = instancesLoading || definitionsLoading;
  const error = instancesError || definitionsError;

  // Filter definitions by category and archive status
  const filteredDefinitions = (definitionsData?.definitions ?? []).filter((def) => {
    if (!showArchived && def.status === 'archived') return false;
    if (showArchived && def.status !== 'archived') return false;
    if (categoryFilter === 'all') return true;
    return TASK_CATEGORY_MAP[def.task_type] === categoryFilter;
  });

  // Count definitions per category (excluding archived)
  const categoryCounts = (definitionsData?.definitions ?? []).reduce(
    (acc, def) => {
      if (def.status !== 'archived') {
        const cat = TASK_CATEGORY_MAP[def.task_type];
        acc[cat] = (acc[cat] || 0) + 1;
        acc.all = (acc.all || 0) + 1;
      }
      return acc;
    },
    {} as Record<string, number>,
  );

  const archivedCount = (definitionsData?.definitions ?? []).filter(
    (d) => d.status === 'archived'
  ).length;

  if (!selectedTank) {
    return (
      <Container size="lg" py="xl">
        <NoTankSelected purpose="to manage tasks" />
      </Container>
    );
  }

  // Count badges
  const overdueCount = instancesData?.overdue_count ?? 0;
  const dueTodayCount = instancesData?.due_today_count ?? 0;

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Title order={1}>Tasks</Title>
          <Button leftSection={<IconPlus size={16} />} onClick={handleOpenCreate}>
            Create Task
          </Button>
        </Group>

        {/* Notification Reminder Banner */}
        {shouldShowReminder && (
          <Alert
            icon={<IconBell size={16} />}
            color="blue"
            variant="light"
          >
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm">
                Want reminders for your tasks?
              </Text>
              <Anchor component={Link} to="/settings?tab=notifications" size="sm" fw={500}>
                Set up notifications
              </Anchor>
            </Group>
          </Alert>
        )}

        {/* Error State */}
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {error instanceof Error ? error.message : 'Failed to load tasks'}
          </Alert>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tabs.List>
            <Tabs.Tab
              value="dashboard"
              leftSection={<IconCalendarEvent size={14} />}
              rightSection={
                (overdueCount > 0 || dueTodayCount > 0) && (
                  <Group gap={4}>
                    {overdueCount > 0 && (
                      <Badge size="xs" color="red" variant="filled">
                        {overdueCount}
                      </Badge>
                    )}
                    {dueTodayCount > 0 && (
                      <Badge size="xs" color="orange" variant="filled">
                        {dueTodayCount}
                      </Badge>
                    )}
                  </Group>
                )
              }
            >
              Dashboard
            </Tabs.Tab>
            <Tabs.Tab value="templates" leftSection={<IconTemplate size={14} />}>
              Templates
            </Tabs.Tab>
            <Tabs.Tab value="history" leftSection={<IconHistory size={14} />}>
              History
            </Tabs.Tab>
          </Tabs.List>

          {/* Loading State */}
          {isLoading && (
            <Group justify="center" py="xl">
              <Loader role="status" />
            </Group>
          )}

          {/* Dashboard Tab */}
          <Tabs.Panel value="dashboard" pt="md">
            {!isLoading && instancesData && (
              <Stack gap="lg">
                {instancesData.instances.length === 0 ? (
                  <Paper p="xl" withBorder ta="center">
                    <Stack align="center" gap="md">
                      <IconCheck size={48} color="gray" />
                      <Text c="dimmed">No tasks due</Text>
                      <Text size="sm" c="dimmed">
                        All caught up! Create a new task to get started.
                      </Text>
                      <Button variant="light" onClick={handleOpenCreate}>
                        Create your first task
                      </Button>
                    </Stack>
                  </Paper>
                ) : (
                  <Stack gap="md">
                    {groupInstancesByUrgency(instancesData.instances).map((group) => (
                      <SectionGroup
                        key={group.key}
                        group={group}
                        isFixedSchedule={isFixedSchedule}
                        getInstanceScheduleLabel={getInstanceScheduleLabel}
                        getInstanceCompactScheduleLabel={getInstanceCompactScheduleLabel}
                        onComplete={handleComplete}
                        onSnooze={handleSnooze}
                        onSkip={handleSkip}
                        onEditTask={handleEditTaskFromInstance}
                        {...(group.key === 'upcoming' ? {
                          initialLimit: 10,
                          expanded: upcomingExpanded,
                          onExpand: () => setUpcomingExpanded(true),
                          pageSize: 25,
                          currentPage: upcomingPage,
                          onPageChange: setUpcomingPage,
                        } : {})}
                      />
                    ))}
                  </Stack>
                )}

                <TaskActivityTimeline
                  completions={timelineCompletionsData?.completions ?? []}
                  dateRange={timelineDateRange}
                  onDateRangeChange={setTimelineDateRange}
                />
              </Stack>
            )}
          </Tabs.Panel>

          {/* Templates Tab (Definitions) */}
          <Tabs.Panel value="templates" pt="md">
            {!isLoading && definitionsData && (
              <Stack gap="md">
                {/* Category sub-tabs + archived toggle */}
                <Group justify="space-between">
                  <SegmentedControl
                    value={showArchived ? 'archived' : categoryFilter}
                    onChange={(value) => {
                      if (value === 'archived') {
                        setShowArchived(true);
                        setCategoryFilter('all');
                      } else {
                        setShowArchived(false);
                        setCategoryFilter(value as 'all' | TaskCategory);
                      }
                    }}
                    data={[
                      { label: `All (${categoryCounts.all || 0})`, value: 'all' },
                      { label: `Water Mgmt (${categoryCounts.water_management || 0})`, value: 'water_management' },
                      { label: `Maintenance (${categoryCounts.maintenance || 0})`, value: 'maintenance' },
                      { label: `Custom (${categoryCounts.custom || 0})`, value: 'custom' },
                      ...(archivedCount > 0
                        ? [{ label: `Archived (${archivedCount})`, value: 'archived' }]
                        : []),
                    ]}
                    size="sm"
                  />
                </Group>

                {/* Task cards */}
                {filteredDefinitions.length === 0 ? (
                  <Paper p="xl" withBorder ta="center">
                    <Stack align="center" gap="md">
                      <Text c="dimmed">
                        {showArchived
                          ? 'No archived tasks'
                          : 'No tasks in this category'}
                      </Text>
                      {!showArchived && (
                        <Button variant="light" onClick={handleOpenCreate}>
                          Create a task
                        </Button>
                      )}
                    </Stack>
                  </Paper>
                ) : (
                  <Stack gap="sm">
                    {filteredDefinitions.map((definition) => (
                      <Card
                        key={definition.id}
                        withBorder
                        data-testid="task-definition-card"
                        style={{
                          cursor: 'pointer',
                          opacity: definition.status === 'archived' ? 0.7 : 1,
                        }}
                        styles={{
                          root: {
                            transition: 'background-color 150ms ease',
                            '&:hover': {
                              backgroundColor: 'var(--mantine-color-gray-1)',
                            },
                          },
                        }}
                        onClick={() => handleEditDefinition(definition)}
                      >
                        <Group justify="space-between">
                          <div style={{ flex: 1 }}>
                            <Group gap="xs">
                              <Text fw={500}>{definition.name}</Text>
                              <Badge
                                color={TASK_TYPE_COLORS[definition.task_type]}
                                variant="light"
                                size="sm"
                              >
                                {TASK_TYPE_LABELS[definition.task_type]}
                              </Badge>
                              <Badge variant="outline" size="sm">
                                {isRecurring(definition) ? 'Recurring' : 'One-time'}
                              </Badge>
                              {definition.recurrence?.max_recurrences != null && definition.completion_count != null && (
                                <Badge variant="light" color="violet" size="sm">
                                  {definition.completion_count} of {definition.recurrence.max_recurrences}
                                </Badge>
                              )}
                              {definition.status === 'paused' && (
                                <Badge color="yellow" size="sm">Paused</Badge>
                              )}
                              {definition.status === 'archived' && (
                                <Badge color="gray" size="sm">Archived</Badge>
                              )}
                            </Group>
                            {definition.equipment_name && (
                              <Text size="sm" c="dimmed">{definition.equipment_name}</Text>
                            )}
                          </div>

                          <Group gap="xs">
                            {/* Inline scheduling toggle (only for non-archived, scheduled tasks) */}
                            {definition.status !== 'archived' && isRecurring(definition) && (
                              <Tooltip
                                label={definition.status === 'active' ? 'Scheduling active' : 'Scheduling paused'}
                              >
                                <Switch
                                  checked={definition.status === 'active'}
                                  disabled={pauseMutation.isPending || resumeMutation.isPending}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    if (definition.status === 'active') {
                                      pauseMutation.mutate(definition.id);
                                    } else {
                                      resumeMutation.mutate(definition.id);
                                    }
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  size="sm"
                                  color="green"
                                  aria-label={`Toggle scheduling for ${definition.name}`}
                                />
                              </Tooltip>
                            )}

                            {/* Log completion + Three-dot menu */}
                            {definition.status === 'active' && (
                              <Button
                                size="xs"
                                variant="light"
                                leftSection={<IconCircleCheck size={14} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleLogCompletion(definition);
                                }}
                              >
                                Log Completion
                              </Button>
                            )}
                            <Menu position="bottom-end">
                              <Menu.Target>
                                <ActionIcon
                                  variant="subtle"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <IconDotsVertical size={16} />
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown>
                                {definition.status !== 'archived' && (
                                  <Menu.Item
                                    leftSection={<IconPencil size={14} />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditDefinition(definition);
                                    }}
                                  >
                                    Edit
                                  </Menu.Item>
                                )}

                                {definition.status === 'archived' ? (
                                  <Menu.Item
                                    leftSection={<IconArchiveOff size={14} />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      restoreMutation.mutate(definition.id);
                                    }}
                                  >
                                    Restore
                                  </Menu.Item>
                                ) : (
                                  <Menu.Item
                                    leftSection={<IconArchive size={14} />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setArchiveConfirmId(definition.id);
                                    }}
                                  >
                                    Archive
                                  </Menu.Item>
                                )}

                                <Menu.Divider />
                                {definition.status === 'active' && (
                                  <Menu.Item
                                    leftSection={<IconCircleCheck size={14} />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleLogCompletion(definition);
                                    }}
                                  >
                                    Log Completion
                                  </Menu.Item>
                                )}
                                <Menu.Item
                                  leftSection={<IconTrash size={14} />}
                                  color="red"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteConfirmId(definition.id);
                                  }}
                                >
                                  Delete permanently
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          </Group>
                        </Group>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Stack>
            )}
          </Tabs.Panel>

          {/* History Tab */}
          <Tabs.Panel value="history" pt="md">
            {!isLoading && <TaskCompletionHistory />}
          </Tabs.Panel>

        </Tabs>
      </Stack>

      {/* Create/Edit Task Definition Modal */}
      <Modal
        opened={formOpened}
        onClose={handleFormCancel}
        title={editingDefinition ? 'Edit Task' : 'Create Task'}
        size="lg"
        fullScreen={isMobile ?? false}
        closeOnClickOutside={!formDirty}
        scrollAreaComponent={ScrollArea.Autosize}
      >
        <TaskDefinitionForm
          definition={editingDefinition || undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isLoading={createMutation.isPending || updateMutation.isPending}
          onDelete={(id) => {
            setFormDirty(false);
            closeForm();
            setEditingDefinition(null);
            setDeleteConfirmId(id);
          }}
          onDirtyChange={setFormDirty}
        />
      </Modal>

      {/* Archive Confirmation Modal */}
      <Modal
        opened={!!archiveConfirmId}
        onClose={() => setArchiveConfirmId(null)}
        title="Archive Task"
        size="sm"
      >
        <Stack gap="md">
          <Text>
            Archive this task? It will be hidden from view, but all completion
            history will be preserved. You can restore it at any time.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setArchiveConfirmId(null)}>
              Cancel
            </Button>
            <Button
              color="blue"
              onClick={() => {
                if (archiveConfirmId) {
                  archiveMutation.mutate(archiveConfirmId);
                  setArchiveConfirmId(null);
                }
              }}
              loading={archiveMutation.isPending}
            >
              Archive
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        title="Delete Task Permanently"
        size="sm"
      >
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            This action cannot be undone.
          </Alert>
          <Text>
            Permanently delete this task and all completion history? This will
            remove the task definition, all scheduled instances, and all records
            of past completions.
          </Text>
          <Text size="sm" c="dimmed">
            If you just want to hide this task, use Archive instead.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => {
                if (deleteConfirmId) {
                  deleteMutation.mutate(deleteConfirmId);
                  setDeleteConfirmId(null);
                }
              }}
              loading={deleteMutation.isPending}
            >
              Delete permanently
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Unsaved Changes Warning Modal */}
      <Modal
        opened={unsavedWarningOpen}
        onClose={closeUnsavedWarning}
        title="Unsaved Changes"
        size="sm"
      >
        <Stack gap="md">
          <Text>
            You have unsaved changes. Are you sure you want to discard them?
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeUnsavedWarning}>
              Keep Editing
            </Button>
            <Button
              color="red"
              onClick={handleDiscardChanges}
            >
              Discard Changes
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Complete Task Modal */}
      <CompleteTaskModal
        opened={!!completeModalInstance}
        onClose={() => setCompleteModalInstance(null)}
        instance={completeModalInstance}
        onComplete={handleCompleteSubmit}
        onEditTask={() => {
          if (completeModalInstance) {
            handleEditTaskFromInstance(completeModalInstance);
          }
        }}
        isLoading={completeMutation.isPending}
      />

      {/* Snooze Task Modal */}
      <SnoozeTaskModal
        opened={!!snoozeModalInstance}
        onClose={() => setSnoozeModalInstance(null)}
        instance={snoozeModalInstance}
        onSnooze={handleSnoozeSubmit}
        isLoading={snoozeMutation.isPending}
      />

      {/* Log Completion Modal */}
      <LogCompletionModal
        opened={!!logCompletionDefinition}
        onClose={() => setLogCompletionDefinition(null)}
        definition={logCompletionDefinition}
        onSubmit={handleLogCompletionSubmit}
        isLoading={logCompletionMutation.isPending}
      />

      {/* Notification Onboarding Modal */}
      <NotificationOnboardingModal
        opened={onboardingModalOpen}
        onClose={closeOnboardingModal}
      />
    </Container>
  );
}
